
	import { defineComponent, PropType } from 'vue';

	interface PdfFindBar {
		open: () => void;
	}

	interface PdfViewerApplication {
		initializedPromise: Promise<void>;
		findBar?: PdfFindBar;
		open: (args: { url: string; originalUrl?: string }) => Promise<void>;
	}

	interface PdfViewerWindow extends Window {
		PDFViewerApplication?: PdfViewerApplication;
	}

	function dataUriToBlobUrl(dataUri: string): string {
		const [header, base64] = dataUri.split(',');
		const mime = header.match(/:(.*?);/)?.[1] ?? 'application/pdf';
		const byteString = atob(base64);
		const ab = new ArrayBuffer(byteString.length);
		const ia = new Uint8Array(ab);
		for (let i = 0; i < byteString.length; i++)
			ia[i] = byteString.charCodeAt(i);
		const blob = new Blob([ab], { type: mime });
		return URL.createObjectURL(blob);
	}

	export default defineComponent({
		name: 'PdfViewer',

		props: {
			source: {
				type: String as PropType<string | null>,
				default: null,
			},
			fileName: {
				type: String,
				default: 'document.pdf',
			},
			loading: {
				type: Boolean,
				default: false,
			},
			loadingMessage: {
				type: String,
				default: 'Đang tạo file PDF...',
			},
		},

		data() {
			return {
				viewerReady: false,
				blobUrl: null as string | null,
				viewerUrl: `${process.env.BASE_URL}pdfjs/web/viewer.html`,
			};
		},

		watch: {
			source: {
				handler() {
					this.openPdfInViewer();
				},
			},
		},

		mounted() {
			window.addEventListener('keydown', this.onKeyDown, true);
		},

		beforeUnmount() {
			window.removeEventListener('keydown', this.onKeyDown, true);
			this.revokeBlobUrl();
		},

		methods: {
			getViewerApp(): PdfViewerApplication | null {
				const frame = this.$refs.pdfFrame as HTMLIFrameElement | undefined;
				const win = frame?.contentWindow as PdfViewerWindow | null;
				return win?.PDFViewerApplication ?? null;
			},

			onKeyDown(event: KeyboardEvent) {
				if (!this.source || !this.viewerReady) return;

				const isFindShortcut =
					(event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f';
				if (!isFindShortcut) return;

				const app = this.getViewerApp();
				if (!app?.findBar) return;

				event.preventDefault();
				event.stopPropagation();

				const frame = this.$refs.pdfFrame as HTMLIFrameElement | undefined;
				frame?.focus();
				app.findBar.open();
			},

			revokeBlobUrl() {
				if (this.blobUrl) {
					URL.revokeObjectURL(this.blobUrl);
					this.blobUrl = null;
				}
			},

			resolvePdfUrl(source: string): string {
				if (source.startsWith('data:')) {
					this.revokeBlobUrl();
					this.blobUrl = dataUriToBlobUrl(source);
					return this.blobUrl;
				}
				return source;
			},

			async onFrameLoad() {
				const app = this.getViewerApp();
				if (!app) return;

				try {
					await app.initializedPromise;
					this.viewerReady = true;
					await this.openPdfInViewer();
				} catch (err) {
					console.error('[PdfViewer] viewer init failed:', err);
				}
			},

			async openPdfInViewer() {
				if (!this.source || !this.viewerReady) return;

				const app = this.getViewerApp();
				if (!app) return;

				try {
					await app.initializedPromise;
					const url = this.resolvePdfUrl(this.source);
					await app.open({ url, originalUrl: this.fileName });
				} catch (err) {
					console.error('[PdfViewer] open failed:', err);
				}
			},
		},
	});
